import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderOpen } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  DataTable,
  type DataTableColumn,
  EmptyState,
  H2,
  Mono,
  PageHeader,
  Stat,
  Text,
} from "@lagoon/ui";

import { api, type Me, type OwnerDetail } from "../api.js";
import { AccountStateBadge, SiteStateBadge } from "../components/badges.js";
import { ReasonAction } from "../components/reason-action.js";
import { formatBytes } from "../format.js";

type OwnedSite = OwnerDetail["sites"][number];

/**
 * One person, and everything an operator can do to their account.
 *
 * `GET /api/owners/:id` and the three mutations under it were implemented and
 * audited on the admin service with no client at all: search listed owners,
 * none of the cells was a link, and suspending somebody was a curl-only
 * operation (LGN-125, audit M8). This is that client, and it is deliberately
 * `SiteScreen` with the nouns changed, because an operator who has learned one
 * of these screens has learned both.
 *
 * The distinction the site screen draws holds here too: suspending an owner
 * stops them signing in and leaves their sites serving. Taking the content down
 * is a separate decision made on each site, which is why the sites below are
 * links rather than a "suspend all" button.
 */
export function OwnerScreen({ ownerId, me }: { ownerId: string; me: Me }): React.JSX.Element {
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ["owner", ownerId], queryFn: () => api.owner(ownerId) });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["owner", ownerId] });
    void queryClient.invalidateQueries({ queryKey: ["search"] });
    void queryClient.invalidateQueries({ queryKey: ["health"] });
  }, [queryClient, ownerId]);

  if (detail.isPending) {
    return (
      <Text size="sm" tone="muted">
        Loading…
      </Text>
    );
  }
  if (detail.error) {
    return (
      <Text size="sm" tone="danger" role="alert">
        {detail.error.message}
      </Text>
    );
  }

  const { owner, sites } = detail.data;
  const suspended = owner.suspendedAt !== null;
  const published = sites.filter((site) => site.publishedDeployId !== null);

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        breadcrumb={
          <a href="#/" className="lg:text-caption lg:text-muted-foreground lg:hover:underline">
            &larr; Sites and owners
          </a>
        }
        eyebrow="Owner"
        title={owner.email}
        description={
          <span>
            {owner.name ?? "Name not set"} · plan {owner.plan} ·{" "}
            <a href={`#/org/${owner.orgId}`} className="lg:text-primary lg:hover:underline">
              their organisation
            </a>
          </span>
        }
        actions={
          <>
            {owner.isStaff && <Badge tone="info">Staff</Badge>}
            {!owner.emailVerified && <Badge tone="warning">Address unverified</Badge>}
            <AccountStateBadge suspendedAt={owner.suspendedAt} />
          </>
        }
      />

      {suspended && owner.suspendedReason !== null && (
        <Alert tone="danger">
          <AlertTitle>Suspended</AlertTitle>
          <AlertDescription>{owner.suspendedReason}</AlertDescription>
        </Alert>
      )}

      {/*
        An unverified address is the answer to the support ticket that reads
        "I cannot publish", and from the outside it looks identical to a quota
        problem or a broken deploy. See docs/runbooks/account-recovery.md.
      */}
      {!owner.emailVerified && (
        <Alert tone="warning">
          <AlertTitle>This address is not verified</AlertTitle>
          <AlertDescription>
            They cannot publish until the identity provider confirms it. This is what "cannot
            publish" usually is.
          </AlertDescription>
        </Alert>
      )}

      <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:xl:grid-cols-4">
        <Stat value={sites.length.toLocaleString()} label="Sites" />
        <Stat value={published.length.toLocaleString()} label="Published" />
        <Stat value={owner.quotaSites.toLocaleString()} label="Site quota" />
        <Stat
          value={formatBytes(owner.quotaBytes)}
          label="Storage quota"
          className="lg:[&_[data-slot=stat-value]]:text-h3"
        />
      </div>

      {me.can.operate ? (
        <section className="lg:flex lg:flex-col lg:gap-4">
          <H2 className="lg:text-h3">Actions</H2>
          <div className="lg:flex lg:flex-wrap lg:items-start lg:gap-3">
            <ReasonAction
              label={suspended ? "Unsuspend owner" : "Suspend owner"}
              danger={!suspended}
              hint={
                suspended
                  ? "They can sign in again from their next attempt."
                  : `Stops them signing in from their next attempt. Their ${String(published.length)} published sites keep serving: taking those down is a separate decision on each one.`
              }
              onSubmit={async (reason) => {
                await (suspended
                  ? api.unsuspendOwner(ownerId, reason)
                  : api.suspendOwner(ownerId, reason));
                invalidate();
              }}
            />

            {/*
              Both quotas are administrator actions, because the service says so
              (`POST /api/owners/:id/quota` is admin-only) and because they are
              written onto the organisation's entitlements, which billing owns.
              They are absent for an operator rather than disabled, the way the
              screening actions are absent for an auditor.
            */}
            {me.can.administer && (
              <>
                <ReasonAction
                  label="Set site quota"
                  field={{
                    label: "Sites",
                    type: "number",
                    initialValue: String(owner.quotaSites),
                    hint: "How many sites this account may hold.",
                    validate: quotaProblem,
                  }}
                  hint="Writes the entitlement on their organisation, which is what enforces it."
                  onSubmit={async (reason, value) => {
                    await api.setOwnerQuota(ownerId, { quotaSites: Number(value), reason });
                    invalidate();
                  }}
                />

                <ReasonAction
                  label="Set storage quota"
                  field={{
                    label: "Bytes",
                    type: "number",
                    initialValue: String(owner.quotaBytes),
                    hint: "In bytes. 1 GB is 1073741824.",
                    validate: quotaProblem,
                  }}
                  hint="Writes the entitlement on their organisation, which is what enforces it."
                  onSubmit={async (reason, value) => {
                    await api.setOwnerQuota(ownerId, { quotaBytes: Number(value), reason });
                    invalidate();
                  }}
                />
              </>
            )}
          </div>

          {!me.can.administer && (
            <Text size="sm" tone="muted">
              Changing a quota is an administrator action, because it is written onto the
              entitlements billing owns.
            </Text>
          )}
        </section>
      ) : (
        <Alert tone="info">
          <AlertTitle>Auditor</AlertTitle>
          <AlertDescription>
            You are signed in as an auditor. This view is read-only for your account.
          </AlertDescription>
        </Alert>
      )}

      <section className="lg:flex lg:flex-col lg:gap-4">
        <H2 className="lg:text-h3">Sites</H2>
        <DataTable
          columns={OWNED_SITE_COLUMNS}
          data={sites}
          caption="Sites this owner holds"
          empty={
            <EmptyState
              icon={<FolderOpen />}
              title="This owner has no sites"
              description="Nothing has been created under this account yet."
              glow={false}
            />
          }
        />
      </section>

      <Text size="sm">
        <a
          href={`#/audit?actor=${encodeURIComponent(owner.email)}`}
          className="lg:text-primary lg:hover:underline"
        >
          See every recorded action by this account
        </a>
      </Text>
    </div>
  );
}

/** A quota is a whole number of things, and zero is a real answer. */
function quotaProblem(value: string): string | null {
  const parsed = Number(value);
  if (value === "" || !Number.isInteger(parsed) || parsed < 0) {
    return "Give a whole number, zero or more.";
  }
  return null;
}

const OWNED_SITE_COLUMNS: DataTableColumn<OwnedSite>[] = [
  {
    accessorKey: "slug",
    header: "Slug",
    cell: (info) => (
      <a
        href={`#/site/${info.row.original.id}`}
        className="lg:font-mono lg:text-primary lg:hover:underline"
      >
        {info.row.original.slug}
      </a>
    ),
  },
  { accessorKey: "name", header: "Name" },
  {
    id: "state",
    header: "State",
    cell: (info) => (
      <SiteStateBadge
        suspendedAt={info.row.original.suspendedAt}
        publishedDeployId={info.row.original.publishedDeployId}
      />
    ),
  },
  {
    id: "id",
    header: "Site",
    cell: (info) => (
      <Mono className="lg:text-caption lg:text-muted-foreground">
        {info.row.original.id.slice(0, 8)}
      </Mono>
    ),
  },
];
